import { useRef, useEffect } from 'react';
import { Panel } from '@/components/Panel';
import { useLatency } from '@/hooks/useLatency';
import { Spinner, EmptyState, StatusBadge } from '@00start/ui';
import { formatDuration } from '@/utils/chart-helpers';

// ---------------------------------------------------------------------------
// Sparkline sub-component
// ---------------------------------------------------------------------------

interface SparklineProps {
  values: number[];
}

function Sparkline({ values }: SparklineProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = canvas;
    ctx.clearRect(0, 0, width, height);

    if (values.length < 2) return;

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    const toX = (i: number) => (i / (values.length - 1)) * width;
    const toY = (v: number) => height - ((v - min) / range) * (height - 2) - 1;

    ctx.beginPath();
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.7)'; // slate-400 translucent
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';

    values.forEach((v, i) => {
      const x = toX(i);
      const y = toY(v);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });

    ctx.stroke();
  }, [values]);

  return <canvas ref={canvasRef} width={60} height={20} style={{ display: 'block' }} />;
}

// ---------------------------------------------------------------------------
// Threshold helper
// ---------------------------------------------------------------------------

type ColorKey = 'critical' | 'high' | 'low';

function p95ColorKey(p95Ms: number): ColorKey {
  if (p95Ms > 1000) return 'critical';
  if (p95Ms > 500) return 'high';
  return 'low';
}

// ---------------------------------------------------------------------------
// LatencyTable
// ---------------------------------------------------------------------------

export function LatencyTable() {
  const { data, loading } = useLatency();

  const services = data?.services ? Object.entries(data.services) : [];

  return (
    <Panel title="Latency Table" mobilePriority={5}>
      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      ) : services.length === 0 ? (
        <EmptyState title="No latency data" description="No latency history available" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] text-slate-400 border-collapse">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="text-left py-1.5 pr-3 font-medium text-slate-300 whitespace-nowrap">
                  Service
                </th>
                <th className="text-right py-1.5 px-3 font-medium text-slate-300 whitespace-nowrap">
                  p50
                </th>
                <th className="text-right py-1.5 px-3 font-medium text-slate-300 whitespace-nowrap">
                  p95
                </th>
                <th className="text-right py-1.5 px-3 font-medium text-slate-300 whitespace-nowrap">
                  p99
                </th>
                <th className="py-1.5 pl-3 font-medium text-slate-300 whitespace-nowrap">
                  History
                </th>
              </tr>
            </thead>
            <tbody>
              {services.map(([key, svc]) => {
                const colorKey = p95ColorKey(svc.p95);
                const sparkValues = svc.history.map((h) => h.latencyMs);
                return (
                  <tr
                    key={key}
                    className="border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors"
                  >
                    <td className="py-1.5 pr-3 text-slate-300 whitespace-nowrap font-mono">
                      {svc.name}
                    </td>
                    <td className="py-1.5 px-3 text-right font-mono whitespace-nowrap">
                      {formatDuration(svc.p50)}
                    </td>
                    <td className="py-1.5 px-3 text-right whitespace-nowrap">
                      <span className="inline-flex items-center justify-end gap-1.5">
                        <span className="font-mono">{formatDuration(svc.p95)}</span>
                        <StatusBadge colorKey={colorKey} />
                      </span>
                    </td>
                    <td className="py-1.5 px-3 text-right font-mono whitespace-nowrap">
                      {formatDuration(svc.p99)}
                    </td>
                    <td className="py-1.5 pl-3">
                      <Sparkline values={sparkValues} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
